"use client";
import { useState } from "react";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { COOKIE_KEYS, setCookie } from "@portal/app/_common/lib/headers";
import {
  EXPERIMENTS,
  Experiments,
  getClientExperiments,
  resolvedExperiments,
} from "@portal/app/_common/lib/experiments";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";

type ExperimentsLabProps = {
  open: boolean;
  onClose: () => void;
};

export const ExperimentsLab = ({ open, onClose }: ExperimentsLabProps) => {
  const [experiments, setExperiments] = useState(getClientExperiments());

  const experimentEntries = Object.entries(experiments).sort(([a], [b]) =>
    resolvedExperiments[b as keyof typeof resolvedExperiments] === undefined
      ? 1
      : resolvedExperiments[a as keyof typeof resolvedExperiments] === undefined
        ? -1
        : 0
  );
  const activeExperiments = experimentEntries.filter(
    ([name]) => resolvedExperiments[name as keyof typeof resolvedExperiments] === undefined
  );
  const inactiveExperiments = experimentEntries.filter(
    ([name]) => resolvedExperiments[name as keyof typeof resolvedExperiments] !== undefined
  );

  return (
    <SwipeableDrawer
      id="experiments-lab"
      open={open}
      onClose={onClose}
      onOpen={() => void 0}
      disableSwipeToOpen
      anchor="bottom"
      sx={{
        "& .MuiDrawer-paper": {
          maxHeight: "90vh",
          overflow: "auto",
        },
      }}
      data-testid="experiments-lab"
    >
      <Paper sx={{ p: 2 }}>
        <Typography variant="new_h3">Active Experiments</Typography>
        <Typography variant="caption" sx={{ p: 2, display: "flex" }}>
          These experiments are available to you as an admin. If you want to test a specific
          experiment group, you can choose your group below.
        </Typography>
        <List>
          {activeExperiments.map(([experiment, currentValue]) => {
            return (
              <ExperimentListItem
                sx={{
                  gap: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  overflowX: "scroll",
                }}
                key={experiment}
              >
                <ListItemText primary={experiment} />
                <ButtonGroup variant="outlined">
                  {Object.entries(EXPERIMENTS[experiment as keyof Experiments] ?? {}).map(
                    ([groupValue, description]: [string, string]) => (
                      <Button
                        key={description}
                        variant={
                          (currentValue as Experiments[keyof Experiments]) === Number(groupValue)
                            ? "contained"
                            : "outlined"
                        }
                        onClick={() => {
                          setExperiments((prev) => {
                            const newExperiments = {
                              ...prev,
                              [experiment]: Number(groupValue),
                            };
                            setCookie(COOKIE_KEYS.EXPERIMENTS, JSON.stringify(newExperiments));
                            return newExperiments;
                          });
                        }}
                        disabled={
                          resolvedExperiments[experiment as keyof typeof resolvedExperiments] !==
                          undefined
                        }
                      >
                        {description}
                      </Button>
                    )
                  )}
                </ButtonGroup>
              </ExperimentListItem>
            );
          })}
        </List>
        <Typography variant="new_h3">Resolved Experiments</Typography>
        <Typography variant="caption" sx={{ p: 2, display: "flex" }}>
          These are experiments that are still in code, but have been resolved. These cannot be
          configured.
        </Typography>
        <List>
          {inactiveExperiments.map(([experiment, currentValue]) => {
            return (
              <ExperimentListItem
                sx={{
                  gap: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  overflowX: "scroll",
                }}
                key={experiment}
              >
                <ListItemText primary={experiment} />
                <ButtonGroup variant="outlined">
                  {Object.entries(EXPERIMENTS[experiment as keyof Experiments] ?? {}).map(
                    ([groupValue, description]: [string, string]) => (
                      <Button
                        key={description}
                        variant={
                          (currentValue as Experiments[keyof Experiments]) === Number(groupValue)
                            ? "contained"
                            : "outlined"
                        }
                        onClick={() => {
                          setExperiments((prev) => {
                            const newExperiments = {
                              ...prev,
                              [experiment]: Number(groupValue),
                            };
                            setCookie(COOKIE_KEYS.EXPERIMENTS, JSON.stringify(newExperiments));
                            return newExperiments;
                          });
                        }}
                        disabled={resolvedExperiments.hasOwnProperty(experiment)}
                      >
                        {description}
                      </Button>
                    )
                  )}
                </ButtonGroup>
              </ExperimentListItem>
            );
          })}
        </List>
      </Paper>
    </SwipeableDrawer>
  );
};

const ExperimentListItem = styled(ListItem)`
  scrollbar-width: none;
  // Hide scrollbar on Safari

  &::-webkit-scrollbar {
    display: none;
  }
`;
